/**
 * Product Import Page
 * Story Q-IMP-1: Beszállítói termékkatalógus XLSX import
 *
 * Workflow:
 * 1. Upload XLSX file
 * 2. Select supplier
 * 3. Map columns (preview first 5 rows)
 * 4. Execute import
 * 5. Show results
 */

import {
  type ColumnMapping,
  type ImportSummary,
  type XlsxPreview,
  PRODUCT_FIELDS,
  getSavedMapping,
  importXlsx,
  previewXlsx,
} from '@/api/product-import';
import { Button, Card, CardContent } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_SUPPLIERS } from './mock-data';

type ImportStep = 'upload' | 'mapping' | 'importing' | 'result';

export function ProductImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [supplierId, setSupplierId] = useState<string>('');
  const [preview, setPreview] = useState<XlsxPreview | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({ name: '', price: '' });
  const [updateExisting, setUpdateExisting] = useState(false);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ============================================
  // STEP 1: Upload + Supplier selection
  // ============================================

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setLoading(true);

    try {
      const res = await previewXlsx(selectedFile);
      setPreview(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fajl feldolgozasi hiba');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleProceedToMapping = useCallback(async () => {
    if (!supplierId || !preview) return;

    // Try to load saved mapping for this supplier
    try {
      const saved = await getSavedMapping(supplierId);
      if (saved.data.mapping) {
        setMapping(saved.data.mapping);
      } else {
        // Auto-detect: try to match headers to fields
        const autoMapping: ColumnMapping = { name: '', price: '' };
        const headers = preview.headers.map(h => h.toLowerCase());

        for (const field of PRODUCT_FIELDS) {
          const matchPatterns = getAutoMatchPatterns(field.key);
          const matchIdx = headers.findIndex(h => matchPatterns.some(p => h.includes(p)));
          if (matchIdx >= 0) {
            const headerValue = preview.headers[matchIdx];
            if (headerValue !== undefined) {
              autoMapping[field.key] = headerValue;
            }
          }
        }

        setMapping(autoMapping);
      }
    } catch {
      // Ignore saved mapping load errors
      setMapping({ name: '', price: '' });
    }

    setStep('mapping');
  }, [supplierId, preview]);

  // ============================================
  // STEP 2: Column mapping
  // ============================================

  const handleMappingChange = useCallback((fieldKey: keyof ColumnMapping, headerValue: string) => {
    setMapping(prev => ({
      ...prev,
      [fieldKey]: headerValue || undefined,
    }));
  }, []);

  const isMappingValid = mapping.name !== '' && mapping.price !== '';

  // ============================================
  // STEP 3: Execute import
  // ============================================

  const handleImport = useCallback(async () => {
    if (!file || !supplierId || !isMappingValid) return;

    setStep('importing');
    setError(null);
    setLoading(true);

    try {
      const res = await importXlsx(file, supplierId, mapping, updateExisting);
      setResult(res.data);
      setStep('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import hiba');
      setStep('mapping');
    } finally {
      setLoading(false);
    }
  }, [file, supplierId, mapping, updateExisting, isMappingValid]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Beszallitoi katalogus import</h1>
          <p className="text-muted-foreground mt-1">XLSX fajl importalasa a termek katalogusba</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/products')}>
          Vissza
        </Button>
      </div>

      {/* Error display */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm">
        <StepIndicator label="1. Feltoltes" active={step === 'upload'} done={step !== 'upload'} />
        <span className="text-muted-foreground">&rarr;</span>
        <StepIndicator
          label="2. Oszlop hozzarendeles"
          active={step === 'mapping'}
          done={step === 'importing' || step === 'result'}
        />
        <span className="text-muted-foreground">&rarr;</span>
        <StepIndicator label="3. Eredmeny" active={step === 'result'} done={false} />
      </div>

      {/* STEP 1: Upload */}
      {step === 'upload' && (
        <Card>
          <CardContent className="space-y-6 pt-6">
            {/* File upload */}
            <div>
              <label className="block text-sm font-medium mb-2">XLSX fajl feltoltes</label>
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {file ? (
                  <div>
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    {preview && (
                      <p className="text-sm text-green-600 mt-1">
                        {preview.headers.length} oszlop, {preview.totalRows} adatsor
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-muted-foreground">
                      Kattints ide vagy huzd ide az XLSX fajlt
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">Max. 10 MB</p>
                  </div>
                )}
              </div>
            </div>

            {/* Supplier selector */}
            <div>
              <label className="block text-sm font-medium mb-2">Beszallito</label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm bg-background"
                value={supplierId}
                onChange={e => setSupplierId(e.target.value)}
              >
                <option value="">-- Valassz beszallitot --</option>
                {MOCK_SUPPLIERS.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Preview table */}
            {preview && preview.headers.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">Elonezet (elso 5 sor)</label>
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        {preview.headers.map((h, i) => (
                          <th key={i} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sampleRows.map((row, ri) => (
                        <tr key={ri} className="border-t">
                          {row.map((cell, ci) => (
                            <td key={ci} className="px-3 py-1.5 whitespace-nowrap">
                              {cell || <span className="text-muted-foreground italic">ures</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleProceedToMapping}
                disabled={!file || !supplierId || !preview || loading}
                loading={loading}
              >
                Tovabb az oszlop hozzarendeleshez
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Column Mapping */}
      {step === 'mapping' && preview && (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <h2 className="text-lg font-semibold">Oszlop hozzarendeles</h2>
            <p className="text-sm text-muted-foreground">
              Rendeld hozza az XLSX oszlopokat a termek mezokhoz. A csillaggal (*) jelolt mezok
              kotelezoek.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PRODUCT_FIELDS.map(field => (
                <div key={field.key} className="flex flex-col gap-1">
                  <label className="text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <select
                    className="rounded-md border px-3 py-2 text-sm bg-background"
                    value={mapping[field.key] ?? ''}
                    onChange={e => handleMappingChange(field.key, e.target.value)}
                  >
                    <option value="">-- Nincs hozzarendelve --</option>
                    {preview.headers.map((h, i) => (
                      <option key={i} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {/* Update existing toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="updateExisting"
                checked={updateExisting}
                onChange={e => setUpdateExisting(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="updateExisting" className="text-sm">
                Meglevo termekek frissitese (SKU egyezes eseten)
              </label>
            </div>

            {/* Mapping preview: show how first row maps */}
            {preview.sampleRows.length > 0 && isMappingValid && (
              <div>
                <h3 className="text-sm font-medium mb-2">Hozzarendeles elonezet (1. sor)</h3>
                <div className="rounded-md border p-3 bg-muted/30 text-sm space-y-1">
                  {PRODUCT_FIELDS.filter(f => mapping[f.key]).map(field => {
                    const headerName = mapping[field.key];
                    const headerIdx = preview.headers.findIndex(h => h === headerName);
                    const sampleValue =
                      headerIdx >= 0 ? preview.sampleRows[0]?.[headerIdx] : undefined;
                    return (
                      <div key={field.key} className="flex gap-2">
                        <span className="font-medium w-40">{field.label}:</span>
                        <span>
                          {sampleValue || (
                            <span className="italic text-muted-foreground">ures</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('upload')}>
                Vissza
              </Button>
              <Button
                onClick={handleImport}
                disabled={!isMappingValid || loading}
                loading={loading}
              >
                Import inditasa ({preview.totalRows} sor)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3: Importing */}
      {step === 'importing' && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-lg font-medium">Import folyamatban...</p>
            <p className="text-sm text-muted-foreground mt-1">
              {preview?.totalRows ?? 0} sor feldolgozasa
            </p>
          </CardContent>
        </Card>
      )}

      {/* STEP 4: Result */}
      {step === 'result' && result && (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <h2 className="text-lg font-semibold">Import eredmeny</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ResultCard
                label={t('product.import.imported')}
                value={result.imported}
                color="text-green-600"
              />
              <ResultCard
                label={t('product.import.updated')}
                value={result.updated}
                color="text-blue-600"
              />
              <ResultCard
                label={t('product.import.skipped')}
                value={result.skipped}
                color="text-yellow-600"
              />
              <ResultCard
                label={t('product.import.totalRows')}
                value={result.totalRows}
                color="text-foreground"
              />
            </div>

            {result.errors.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2">Hibak ({result.errors.length})</h3>
                <div className="max-h-60 overflow-y-auto rounded-md border p-3 bg-muted/30 text-sm space-y-1">
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-red-600 dark:text-red-400">
                      {err}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/products')}>
                Vissza a termekekhez
              </Button>
              <Button
                onClick={() => {
                  setStep('upload');
                  setFile(null);
                  setPreview(null);
                  setResult(null);
                  setError(null);
                }}
              >
                Uj import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function StepIndicator({ label, active, done }: { label: string; active: boolean; done: boolean }) {
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${
        active
          ? 'bg-primary text-primary-foreground'
          : done
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-muted text-muted-foreground'
      }`}
    >
      {label}
    </span>
  );
}

function ResultCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-md border p-4 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

// ============================================
// AUTO-DETECT PATTERNS
// ============================================

function getAutoMatchPatterns(fieldKey: string): string[] {
  const patterns: Record<string, string[]> = {
    name: ['nev', 'name', 'megnevezes', 'termek', 'product', 'cikk'],
    price: ['ar', 'price', 'beszerz', 'netto', 'purchase', 'net'],
    sku: ['sku', 'cikkszam', 'cikksz', 'kod', 'code', 'szam'],
    brand: ['marka', 'brand', 'gyarto', 'manufacturer'],
    category: ['kategoria', 'category', 'csoport', 'group'],
    supplierSku: ['beszallitoi', 'supplier', 'gyari'],
    barcode: ['vonalkod', 'barcode', 'ean', 'gtin'],
    description: ['leiras', 'description', 'desc'],
    model: ['modell', 'model', 'tipus', 'type'],
    unit: ['egyseg', 'unit', 'me', 'mertekegyseg'],
    vatPercent: ['afa', 'vat', 'ado'],
    listPrice: ['lista', 'list', 'brutto', 'gross'],
    sellingPrice: ['elad', 'sell', 'retail'],
  };

  return patterns[fieldKey] ?? [];
}
